import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import {
  Camera, CheckCircle2, PlayCircle, Zap, User, MapPin, Clock, XCircle,
  AlertTriangle, Loader2, ImagePlus, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLES = {
  emergency: "bg-rose-500 text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-amber-500 text-black",
  low: "bg-zinc-700 text-zinc-200",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export default function TechTicketPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [busy, setBusy] = useState("");
  const [showComplete, setShowComplete] = useState(false);
  const [resolution, setResolution] = useState("");
  const [showDecline, setShowDecline] = useState(false);
  const [declineNote, setDeclineNote] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tech/tickets/${id}`);
      setTicket(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Ticket not found");
      nav("/tech");
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setBusy("accept");
    try {
      await api.post(`/maintenance/${id}/accept`);
      toast.success("Accepted — timer stopped");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Accept failed");
    } finally { setBusy(""); }
  };

  const start = async () => {
    setBusy("start");
    try {
      await api.post(`/tech/tickets/${id}/start`);
      toast.success("Marked in progress");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed");
    } finally { setBusy(""); }
  };

  const complete = async () => {
    setBusy("complete");
    try {
      await api.post(`/tech/tickets/${id}/complete`, { resolution: resolution || undefined });
      toast.success("Ticket completed");
      setShowComplete(false);
      setResolution("");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed");
    } finally { setBusy(""); }
  };

  const decline = async () => {
    if (!declineNote.trim()) { toast.error("Add a short reason so admins know"); return; }
    setBusy("decline");
    try {
      await api.post(`/tech/tickets/${id}/decline`, { note: declineNote });
      toast.success("Declined — an admin can reassign");
      nav("/tech");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed");
    } finally { setBusy(""); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/maintenance/${id}/photo`;
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      toast.success("Photo added");
      load();
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally { setUploadingPhoto(false); }
  };

  if (!ticket) {
    return (
      <div className="flex items-center justify-center py-20 text-white/60" data-testid="tech-ticket-loading">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const status = ticket.status || "open";
  const canAccept = ticket.assigned_to === user?.id && !ticket.acknowledged_at && status === "open";
  const canStart = status === "open" && ticket.assigned_to === user?.id;
  const canComplete = status !== "completed" && ticket.assigned_to === user?.id;
  const canDecline = status === "open" && ticket.assigned_to === user?.id && !ticket.acknowledged_at;
  const photos = ticket.photos || [];

  return (
    <div className="pb-32" data-testid="tech-ticket-page">
      {/* Escalation warning */}
      {ticket.escalated_at && (
        <Card className="p-3 mb-4 bg-rose-500/10 border-rose-500/30 flex items-center gap-3"
              data-testid="tech-escalation-alert">
          <AlertTriangle className="h-5 w-5 text-rose-400 shrink-0" />
          <div className="text-sm text-rose-200">
            This ticket was escalated to {ticket.escalated_to_name || "your PM"} at {fmtDateTime(ticket.escalated_at)}.
          </div>
        </Card>
      )}

      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge className={PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.medium}
                 data-testid="tech-ticket-priority">
            {ticket.priority}
          </Badge>
          <Badge variant="outline"
                 className={
                   status === "completed"
                     ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                     : status === "in_progress"
                       ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/40"
                       : "bg-amber-500/15 text-amber-300 border-amber-500/40"
                 }
                 data-testid="tech-ticket-status">
            {status.replace("_", " ")}
          </Badge>
          {ticket.category && (
            <Badge variant="outline" className="border-white/20 text-white/70">
              {ticket.category}
            </Badge>
          )}
        </div>
        <h1 className="text-2xl font-semibold font-display" data-testid="tech-ticket-title">
          {ticket.title}
        </h1>
        <div className="text-xs text-white/50 mt-2 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Unit {ticket.unit || "—"}</span>
          <span className="flex items-center gap-1"><User className="h-3 w-3" /> {ticket.resident_name || "—"}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDateTime(ticket.created_at)}</span>
        </div>
      </div>

      {/* Description */}
      {ticket.description && (
        <Card className="p-4 mb-4 bg-white/[0.03] border-white/10" data-testid="tech-ticket-description">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Description</div>
          <div className="text-sm text-white/90 whitespace-pre-wrap">{ticket.description}</div>
        </Card>
      )}

      {/* Photos */}
      <Card className="p-4 mb-4 bg-white/[0.03] border-white/10" data-testid="tech-photos-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-white/40">
            Photos {photos.length > 0 && `· ${photos.length}`}
          </div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment"
                  className="hidden" data-testid="tech-photo-input"
                  onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          <Button size="sm" variant="outline"
                  className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="tech-add-photo-btn">
            {uploadingPhoto ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading…</>
                            : <><Camera className="h-4 w-4 mr-1" /> Add photo</>}
          </Button>
        </div>
        {photos.length === 0 ? (
          <button
            onClick={() => fileRef.current?.click()}
            className="w-full py-10 border-2 border-dashed border-white/10 rounded-lg text-white/40 text-sm flex flex-col items-center hover:border-orange-500/50 hover:text-orange-300 transition"
            data-testid="tech-photos-empty"
          >
            <ImagePlus className="h-8 w-8 mb-1" />
            Tap to take a site photo
          </button>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="tech-photo-grid">
            {photos.map((p) => (
              <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.path}`}
                 target="_blank" rel="noreferrer"
                 className="aspect-square rounded-md overflow-hidden bg-white/5 border border-white/10">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.path}`}
                     alt="site" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        )}
      </Card>

      {/* Timeline hints */}
      <Card className="p-4 mb-4 bg-white/[0.03] border-white/10" data-testid="tech-timeline">
        <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Timeline</div>
        <div className="space-y-1 text-xs text-white/70">
          <div>Reported: <span className="text-white">{fmtDateTime(ticket.created_at)}</span></div>
          {ticket.assigned_at && (
            <div>Assigned to {ticket.assigned_to_name || "you"}: <span className="text-white">{fmtDateTime(ticket.assigned_at)}</span></div>
          )}
          {ticket.acknowledged_at && (
            <div className="text-emerald-300">
              <CheckCircle2 className="h-3 w-3 inline mr-1" />
              Accepted: {fmtDateTime(ticket.acknowledged_at)}
            </div>
          )}
          {ticket.in_progress_at && (
            <div className="text-indigo-300">
              <PlayCircle className="h-3 w-3 inline mr-1" />
              Started: {fmtDateTime(ticket.in_progress_at)}
            </div>
          )}
          {ticket.completed_at && (
            <div className="text-emerald-300">
              <CheckCircle2 className="h-3 w-3 inline mr-1" />
              Completed: {fmtDateTime(ticket.completed_at)}
            </div>
          )}
          {ticket.escalated_at && (
            <div className="text-rose-300">
              <Zap className="h-3 w-3 inline mr-1" />
              Escalated: {fmtDateTime(ticket.escalated_at)}
            </div>
          )}
        </div>
      </Card>

      {/* Resolution note if completed */}
      {status === "completed" && ticket.resolution && (
        <Card className="p-4 mb-4 bg-emerald-500/10 border-emerald-500/30" data-testid="tech-resolution">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Resolution</div>
          <div className="text-sm text-emerald-100 whitespace-pre-wrap">{ticket.resolution}</div>
        </Card>
      )}

      {/* Complete modal */}
      {showComplete && (
        <Card className="p-4 mb-4 bg-emerald-500/10 border-emerald-500/40" data-testid="tech-complete-modal">
          <div className="text-sm font-medium text-emerald-200 mb-2">
            Add a resolution note (optional but helpful for records)
          </div>
          <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    placeholder="Replaced compressor, tested for 15 min, working normally."
                    rows={4}
                    data-testid="tech-resolution-input" />
          <div className="flex gap-2 mt-3">
            <Button variant="ghost" onClick={() => setShowComplete(false)}
                    className="text-white/70">
              Cancel
            </Button>
            <Button onClick={complete} disabled={busy === "complete"}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
                    data-testid="tech-confirm-complete-btn">
              {busy === "complete" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm complete"}
            </Button>
          </div>
        </Card>
      )}

      {/* Decline modal */}
      {showDecline && (
        <Card className="p-4 mb-4 bg-rose-500/10 border-rose-500/40" data-testid="tech-decline-modal">
          <div className="text-sm font-medium text-rose-200 mb-2">
            Why are you declining this ticket? (Required — admin will see this)
          </div>
          <Textarea value={declineNote} onChange={(e) => setDeclineNote(e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    placeholder="I'm off-site until Thursday — please reassign."
                    rows={3}
                    data-testid="tech-decline-input" />
          <div className="flex gap-2 mt-3">
            <Button variant="ghost" onClick={() => setShowDecline(false)}
                    className="text-white/70">
              Cancel
            </Button>
            <Button onClick={decline} disabled={busy === "decline"}
                    className="flex-1 bg-rose-600 hover:bg-rose-700 h-12 text-base"
                    data-testid="tech-confirm-decline-btn">
              {busy === "decline" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm decline"}
            </Button>
          </div>
        </Card>
      )}

      {/* Bottom action bar — sticky so big buttons are always reachable */}
      {(canAccept || canStart || canComplete || canDecline) && !showComplete && !showDecline && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-zinc-950/95 backdrop-blur"
             data-testid="tech-action-bar">
          <div className="max-w-3xl mx-auto px-4 py-3 flex gap-2">
            {canDecline && (
              <Button variant="ghost"
                      onClick={() => setShowDecline(true)}
                      className="h-14 px-4 text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                      data-testid="tech-decline-btn">
                <XCircle className="h-5 w-5" />
              </Button>
            )}
            {canAccept && (
              <Button onClick={accept} disabled={busy === "accept"}
                      className="flex-1 h-14 text-base bg-emerald-600 hover:bg-emerald-700"
                      data-testid="tech-accept-btn">
                {busy === "accept" ? <Loader2 className="h-5 w-5 animate-spin" /> :
                  <><CheckCircle2 className="h-5 w-5 mr-1" /> Accept ticket</>}
              </Button>
            )}
            {!canAccept && canStart && (
              <Button onClick={start} disabled={busy === "start"}
                      className="flex-1 h-14 text-base bg-indigo-600 hover:bg-indigo-700"
                      data-testid="tech-start-btn">
                {busy === "start" ? <Loader2 className="h-5 w-5 animate-spin" /> :
                  <><PlayCircle className="h-5 w-5 mr-1" /> Start work</>}
              </Button>
            )}
            {canComplete && (
              <Button onClick={() => setShowComplete(true)}
                      className="flex-1 h-14 text-base bg-orange-500 hover:bg-orange-600"
                      data-testid="tech-complete-btn">
                <CheckCircle2 className="h-5 w-5 mr-1" /> Complete
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
